import { apiPost } from '../../../generic/Api_Functions'
import { DOCUMENT_SCOPES } from '../../../generic/qr/qrDocumentResources'

/**
 * The only module of this widget that talks to Sun. Everything above it (the hook, the widget, the
 * dialogs) works against the normalised shape returned here, so swapping the backend contract is a
 * change confined to this file.
 *
 * One endpoint for every read, including the ones that are going to be rejected: that is what makes
 * the audit trail complete without a second "trace" call. A label scanned at the wrong station is
 * exactly what the traceability requirement is about.
 */

export const QR_SCAN_URL = 'mos/qr/scan'

// What a read is for. SHEET opens the element's card, as every read did before story 228; JOB_DETAIL
// asks Sun to resolve, for a fabrication order or a part, the job of that order on this station's
// machine. Sun stores the action of every read in the audit trail.
export const QR_ACTIONS = { SHEET: 'SHEET', JOB_DETAIL: 'JOB_DETAIL' }

/**
 * What Sun resolved for a JOB_DETAIL read of a fabrication order or a part. Every other read — a
 * SHEET read, an element that is not a fabrication one, a part with no order, a screen with no
 * machine — answers NOT_APPLICABLE and opens the element's sheet, as before story 228.
 */
export const QR_MATCH_TYPES = {
  EXACT_ACTIVE: 'EXACT_ACTIVE',
  EXACT_PLANNED: 'EXACT_PLANNED',
  TECHNOLOGY_PLANNED: 'TECHNOLOGY_PLANNED',
  EXACT_FINISHED: 'EXACT_FINISHED',
  MULTIPLE_CANDIDATES: 'MULTIPLE_CANDIDATES',
  NO_JOB_FOR_DEVICE: 'NO_JOB_FOR_DEVICE',
  NOT_APPLICABLE: 'NOT_APPLICABLE'
}

// The four answers that name a single job to open.
export const JOB_MATCH_TYPES = [
  QR_MATCH_TYPES.EXACT_ACTIVE,
  QR_MATCH_TYPES.EXACT_PLANNED,
  QR_MATCH_TYPES.TECHNOLOGY_PLANNED,
  QR_MATCH_TYPES.EXACT_FINISHED
]

/**
 * Whether Sun looked for the next job of an order with no work on this machine and what it found,
 * sent only along with NO_JOB_FOR_DEVICE. NONE: the plant has nothing pending for it. UNKNOWN: the
 * search could not tell. A Sun that does not send it is read as '' — today's behaviour.
 */
export const PENDING_WORK_LOOKUPS = { FOUND: 'FOUND', NONE: 'NONE', UNKNOWN: 'UNKNOWN' }

// Rejections Sun reports back. Anything else is an unexpected failure and gets the generic message.
export const QR_OUTCOMES = {
  RESOLVED: 'RESOLVED',
  NOT_A_MIC_QR: 'NOT_A_MIC_QR',
  UNEXPECTED_TYPE: 'UNEXPECTED_TYPE',
  UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
  INVALID_REQUEST: 'INVALID_REQUEST',
  NOT_FOUND: 'NOT_FOUND'
}

/**
 * Whether the scanned element belongs to the context of the station that read it. Sun answers it
 * against a different context per entity type — a raw-material lot against the machine warehouse it
 * is loaded in, a part or a fabrication order against the active job — so the verdict travels as a
 * verdict and the front only decides how to word it (see contextMismatchKey).
 *
 * UNKNOWN covers "no station in the request", "the element is not bound anywhere" (a lot in general
 * stock) and "the check failed": three situations in which no mismatch can be asserted.
 */
export const QR_CONTEXT_MATCHES = { ACTIVE: 'ACTIVE', FOREIGN: 'FOREIGN', UNKNOWN: 'UNKNOWN' }

// The reader reports 'scanner' | 'camera' | 'manual'; the audit column is uppercase.
const wireSource = (source) => String(source || '').toUpperCase()

/**
 * One document level as Sun sends it, turned into the flat list the panel renders.
 *
 * Sun answers a level per field and sends null for a level that does not exist — a lot with no raw
 * material reference has no reference level at all — so a null is not "empty", it is "no tab". The
 * `resourceType`/`referenceId` pair is exactly what `document/getAll/{resourceType}/{id}` eats, and
 * it comes from the backend rather than from a local mapping so a renamed resource type needs no
 * front release.
 */
const readSource = (source, scope) => {
  if (!source || !source.resourceType || !source.referenceId) return []
  return [{
    scope,
    resourceType: source.resourceType,
    referenceId: source.referenceId,
    name: source.name || '',
    documentCount: source.documentCount || 0
  }]
}

// The same four fields, for an element that hangs off the scanned one rather than being a level of
// it. No scope: a line is not another view of the order, it is a different element.
const readRelated = (node) => {
  if (!node || !node.resourceType || !node.referenceId) return []
  return [{
    resourceType: node.resourceType,
    referenceId: node.referenceId,
    name: node.name || '',
    documentCount: node.documentCount || 0
  }]
}

/**
 * The structure under a scanned order: every active line, and under each one its fabrication orders.
 *
 * Sun sends the WHOLE tree, counters included, and a zero counter where a node has nothing — never a
 * pruned branch. That is what lets the operator see that a line exists and is empty, instead of
 * wondering whether it is missing or just undocumented. `orderLines` is [] for the other five types.
 */
const readOrderLines = (orderLines) => {
  if (!Array.isArray(orderLines)) return []
  return orderLines.flatMap((line) => readRelated(line).map((node) => ({
    ...node,
    fabricationOrders: Array.isArray(line.fabricationOrders)
      ? line.fabricationOrders.flatMap(readRelated)
      : []
  })))
}

// A machine has no code in Sun: the destination is its id and its name (amendment 4). The same shape
// is the machine a job is planned at (amendment 6).
const readDevice = (device) => device && device.id
  ? { id: device.id, name: device.name || '' }
  : {}

// Where the job sits in the flow of its order, "step 2 of 4". Sun sends both or neither; a pair that
// does not hold 1 <= step <= steps is not shown, so it reads as 0 of 0 (amendment 6).
const readStep = (step, steps) => Number.isInteger(step) && Number.isInteger(steps) && step >= 1 && step <= steps
  ? { step, steps }
  : { step: 0, steps: 0 }

// matchType is '' for the pending job of a wrong-machine answer: Sun sends it null there, on purpose,
// so nothing that decides by it ever opens that job (RN34).
const readJob = (job) => job && job.jobReferenceId
  ? {
      jobReferenceId: job.jobReferenceId,
      code: job.code || '',
      name: job.name || '',
      status: job.status || '',
      matchType: job.matchType || '',
      plannedDevice: readDevice(job.plannedDevice),
      ...readStep(job.step, job.steps)
    }
  : {}

const readFabricationOrder = (order) => order && order.id
  ? { id: order.id, code: order.code || '', description: order.description || '' }
  : {}

/**
 * Reads what Sun answered, tolerating a partial response the way normalizeActiveContext does for the
 * active-context poll: the front must keep working while Sun rolls out, and must never crash on a
 * missing field.
 *
 * Nothing is inferred from the label beyond the identity: the header pairs, the document levels and
 * the context verdict are the backend's call, and a lot resolved with no level at all is rendered as
 * "no documents" rather than guessed at — guessing would open someone else's drawings.
 */
export const normalizeScanResponse = (response = {}, parsed = {}) => ({
  outcome: response.outcome || QR_OUTCOMES.RESOLVED,
  entityType: response.entityType || parsed.entityType || '',
  entityId: response.entityId || parsed.id || 0,
  title: response.title || '',
  subtitle: response.subtitle || '',
  // Pairs are rendered in the order they arrive and never indexed by position: Sun drops the empty
  // ones before serialising, so which field sits at which index changes from one lot to the next.
  header: Array.isArray(response.header) ? response.header.filter((field) => field && field.label) : [],
  // Instance first, reference second: the operator's own lot before the article it belongs to.
  documentSources: [
    ...readSource(response.instanceDocumentSource, DOCUMENT_SCOPES.INSTANCE),
    ...readSource(response.referenceDocumentSource, DOCUMENT_SCOPES.REFERENCE)
  ],
  orderLines: readOrderLines(response.orderLines),
  contextMatch: response.contextMatch || QR_CONTEXT_MATCHES.UNKNOWN,
  // 0 when Sun could not write the audit row. Shown to the operator only as a support reference.
  traceId: response.traceId || 0,
  // Story 228. A Sun without these fields answers as a SHEET read did, so their absence is NOT_APPLICABLE.
  // An empty object stands for the null Sun sends: no job, no order, no destination machine.
  matchType: response.matchType || QR_MATCH_TYPES.NOT_APPLICABLE,
  job: readJob(response.job),
  fabricationOrder: readFabricationOrder(response.fabricationOrder),
  matchingJobs: Array.isArray(response.matchingJobs) ? response.matchingJobs.map(readJob).filter((job) => job.jobReferenceId) : [],
  targetDevice: readDevice(response.targetDevice),
  pendingWorkLookup: Object.values(PENDING_WORK_LOOKUPS).includes(response.pendingWorkLookup) ? response.pendingWorkLookup : '',
  // The job that gave the destination machine; Sun sends it only along with targetDevice (RN34).
  pendingJob: readJob(response.pendingJob),
  // The sheet of the part's order, read like any other answer (RN17a).
  fabricationOrderSheet: response.fabricationOrderSheet && response.fabricationOrderSheet.entityType
    ? normalizeScanResponse(response.fabricationOrderSheet)
    : {}
})

/**
 * Sends one scan. `jobPolicies` are the options of the gear's "How the job is chosen" block that Sun
 * reads (`askOperatorOnMultipleJobs`, …); the front-only ones never reach this function. Resolves with
 * the normalised result, or throws the plain object apiF builds ({ status, data, message }) so the
 * caller can branch on the outcome Sun reported.
 *
 * `entityId` travels as 0 when the code is not a MIC QR: parseQrPayload already reports 0 for it and
 * Sun reads it as "no identity", which keeps the request free of nulls.
 */
export const scanQrCode = async ({ parsed = {}, expectedEntityType, deviceId, workerId, action = QR_ACTIONS.SHEET, jobPolicies = {} }) => {
  const isJobDetail = action === QR_ACTIONS.JOB_DETAIL
  const response = await apiPost(QR_SCAN_URL, {
    raw: parsed.raw || '',
    entityType: parsed.isMicQr ? parsed.entityType : '',
    entityId: parsed.isMicQr ? parsed.id : 0,
    expectedEntityType: expectedEntityType || 'Any',
    deviceId: deviceId || 0,
    workerId: workerId || 0,
    source: wireSource(parsed.source),
    action: isJobDetail ? QR_ACTIONS.JOB_DETAIL : QR_ACTIONS.SHEET,
    // Sun applies them only to a JOB_DETAIL read, so a SHEET read keeps today's exact body.
    ...(isJobDetail ? jobPolicies : {})
  })
  return normalizeScanResponse(response, parsed)
}

/** The outcome Sun reported for a failed scan, whatever shape the error body arrived in. */
export const failureOutcome = (error) => {
  const outcome = error?.data?.outcome || ''
  if (outcome !== '') return outcome
  if (error?.status === 404) return QR_OUTCOMES.NOT_FOUND
  return ''
}
